import DunningService from '@/api/dunning.service';
import { timeDay, ascending } from 'd3';
import { generateDunningSequence } from '@/helpers/templates';
import OrgService from '@/api/org.service';
import StatsService from '@/api/stats.service';
import { mapGetters } from 'vuex';
import { LOAD_STATE, SAVE_STATE } from '../shared';
import * as MUTATE from '../mutation-types';
import * as ACT from '../action-types';
import { EmailMergeField, ICampaignBlueprint, IEmail, IEmailBlueprint, IEmailCount, IExclusion, IState, IUser, LoadState } from '../types';

interface IActionContext {
  commit: any;
  dispatch: any;
  state: IState;
  getters: any;
}

const storeCampaignData = (state: IState, { id, data }: { id: string; data: Partial<ICampaignBlueprint> }) => {
  const campaignData = {
    ...state.campaignBlueprints[id],
    ...data,
  };
  state.campaignBlueprints = {
    ...state.campaignBlueprints,
    [id]: campaignData,
  };
};

const storeExclusionData = (state: IState, { id, data }: { id: string; data: Partial<IExclusion> }) => {
  const exclusionData = {
    ...state.dunningExclusions[id],
    ...data,
  };
  state.dunningExclusions = {
    ...state.dunningExclusions,
    [id]: exclusionData,
  };
};

const dunningModule = {
  // dunning object (with dkim etc) lives under org
  state: {
    currentCampaignBlueprintId: null,
    currentEmailBlueprintId: null,
  },

  mutations: {
    [MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA](state: IState, { id, data }: { id: string; data: any }) {
      const campaignBlueprintData = {
        ...state.campaignBlueprints[id],
        ...data,
      };
      state.campaignBlueprints = {
        ...state.campaignBlueprints,
        [id]: campaignBlueprintData,
      };
    },
    [MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_EMAIL_COUNT](state: IState, { id, data }: { id: string; data: IEmailCount }) {
      const emailCount = {
        ...state.campaignBlueprintEmailCounts[id],
        ...data,
      };
      state.campaignBlueprintEmailCounts = {
        ...state.campaignBlueprintEmailCounts,
        [id]: emailCount,
      };
    },
    [MUTATE.SET_CURRENT_DUNNING_CAMPAIGN_BLUEPRINT_ID](state: IState, blueprintId: string) {
      state.currentCampaignBlueprintId = blueprintId;
    },
    [MUTATE.SET_CURRENT_DUNNING_EMAIL_BLUEPRINT_ID](state: IState, blueprintId: string) {
      state.currentEmailBlueprintId = blueprintId;
    },
    [MUTATE.STORE_DUNNING_SETTINGS](state: IState, payload: any) {
      if (state.org?.dunning) {
        state.org.dunning = { ...state.org.dunning, ...payload };
      }
    },
    // does not override entire email
    [MUTATE.STORE_DUNNING_EMAIL_DATA](
      state: IState,
      { campaignId, emailId, data }: { campaignId: string; emailId: string; data: Partial<IEmailBlueprint> }
    ) {
      const campaign = state.campaignBlueprints[campaignId];
      if (campaign) {
        const email = campaign.emails.find((e) => e.guid === emailId);
        if (email) {
          const newEmail = {
            ...email,
            ...data,
          };
          const newEmails = campaign.emails.map((e) => {
            if (e.guid === emailId) {
              return newEmail;
            }
            return e;
          });
          storeCampaignData(state, { id: campaignId, data: { emails: newEmails } });
        }
      }
    },
    // overrides entire email
    [MUTATE.SET_DUNNING_CAMPAIGN_EMAIL](
      state: IState,
      { campaignId, emailId, data }: { campaignId: string; emailId: string; data: IEmailBlueprint }
    ) {
      const campaign = state.campaignBlueprints[campaignId];
      if (campaign) {
        const email = campaign.emails.find((e) => e.guid === emailId);
        if (email) {
          const newEmail = data;
          // store vs set
          // const newEmail = {
          //   ...email,
          //   ...data,
          // };
          const newEmails = campaign.emails.map((e) => {
            if (e.guid === emailId) {
              return newEmail;
            }
            return e;
          });
          storeCampaignData(state, { id: campaignId, data: { emails: newEmails, saveState: SAVE_STATE.UNSAVED } });
        }
      }
    },
    [MUTATE.STORE_DUNNING_EXCLUSION_DATA](state: IState, { id, data }: { id: string; data: any }) {
      const dunningExclusionData = {
        ...state.dunningExclusions[id],
        ...data,
      };
      state.dunningExclusions = {
        ...state.dunningExclusions,
        [id]: dunningExclusionData,
      };
    },
    [MUTATE.STORE_DUNNING_EXCLUSION_BLOCK_COUNT](state: IState, { id, data }: { id: string; data: any }) {
      const dunningExclusionBlockCount = {
        ...state.dunningExclusionBlockCounts[id],
        ...data,
      };
      state.dunningExclusionBlockCounts = {
        ...state.dunningExclusionBlockCounts,
        [id]: dunningExclusionBlockCount,
      };
    },
  },

  actions: {
    // sync dunning info from org and campaign blueprints
    async [ACT.SYNC_DUNNING]({ state, commit, dispatch }: IActionContext, options: any) {
      await dispatch(ACT.SYNC_ORG);
      await Promise.all([dispatch(ACT.SYNC_DUNNING_CAMPAIGN_BLUEPRINTS), dispatch(ACT.SYNC_DUNNING_EXCLUSIONS)]);
    },
    async [ACT.SYNC_DUNNING_CAMPAIGN_BLUEPRINTS]({ state, commit, dispatch }: IActionContext, options: any) {
      try {
        // fetch campaign blueprints and email counts
        state.org?.dunning?.campaignBlueprints?.forEach((id) => {
          commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id, data: { loadState: LOAD_STATE.LOADING } });
        });
        const thirtyDaysAgo = timeDay.floor(timeDay.offset(new Date(), -30));
        const [campaignBlueprints, dunningEmailCounts] = await Promise.all([
          DunningService.getAllCampaignBlueprints(),
          StatsService.dunningEmailCounts({
            filter: {
              startDate: thirtyDaysAgo,
              breakdown: ['campaignBlueprintId'],
            },
          }),
        ]);
        campaignBlueprints?.forEach((campaignBlueprint) => {
          commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, {
            id: campaignBlueprint._id,
            data: {
              ...campaignBlueprint,
              loadState: LOAD_STATE.LOADED,
              saveState: SAVE_STATE.SAVED,
            },
          });
          const emailCount = dunningEmailCounts.find(({ campaignBlueprintId }) => {
            return campaignBlueprintId === campaignBlueprint._id;
          }) || { count: 0, invoiceAmount: 0 };
          commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_EMAIL_COUNT, {
            id: campaignBlueprint._id,
            data: {
              ...emailCount,
              loadState: LOAD_STATE.LOADED,
              saveState: SAVE_STATE.SAVED,
            },
          });
        });
      } catch (e) {
        console.log('Error syncing campaign blueprints', e);
        state.org?.dunning?.campaignBlueprints?.forEach((id) => {
          commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id, data: { loadState: LOAD_STATE.ERROR_LOADING } });
          commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_EMAIL_COUNT, { id, data: { loadState: LOAD_STATE.ERROR_LOADING } });
        });
      }
    },

    async [ACT.SYNC_DUNNING_EXCLUSIONS]({ state, commit, dispatch }: IActionContext, options: any) {
      try {
        // fetch dunning exclusions and block counts
        state.org?.dunning?.exclusions?.forEach((id) => {
          commit(MUTATE.STORE_DUNNING_EXCLUSION_DATA, { id, data: { loadState: LOAD_STATE.LOADING } });
        });
        const thirtyDaysAgo = timeDay.floor(timeDay.offset(new Date(), -30));
        const [exclusions, blockCounts] = await Promise.all([
          DunningService.getAllExclusions(),
          StatsService.blockCounts({
            filter: {
              startDate: thirtyDaysAgo,
              breakdown: ['exclusionId'],
            },
          }),
        ]);
        exclusions?.forEach((exclusion) => {
          commit(MUTATE.STORE_DUNNING_EXCLUSION_DATA, {
            id: exclusion._id,
            data: {
              ...exclusion,
              loadState: LOAD_STATE.LOADED,
              saveState: SAVE_STATE.SAVED,
            },
          });
          const blockCount = blockCounts.find(({ exclusionId }) => {
            return exclusionId === exclusion._id;
          }) || { count: 0 };
          commit(MUTATE.STORE_DUNNING_EXCLUSION_BLOCK_COUNT, {
            id: exclusion._id,
            data: {
              ...blockCount,
              loadState: LOAD_STATE.LOADED,
              saveState: SAVE_STATE.SAVED,
            },
          });
        });
      } catch (e) {
        console.log('Error syncing dunning exclusions', e);
        state.org?.dunning?.exclusions?.forEach((id) => {
          commit(MUTATE.STORE_DUNNING_EXCLUSION_DATA, { id, data: { loadState: LOAD_STATE.ERROR_LOADING } });
          commit(MUTATE.STORE_DUNNING_EXCLUSION_BLOCK_COUNT, { id, data: { loadState: LOAD_STATE.ERROR_LOADING } });
        });
      }
    },
    async [ACT.FETCH_DUNNING_CAMPAIGN_BLUEPRINT]({ commit, state }: IActionContext, id: string) {
      commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id, data: { loadState: LOAD_STATE.LOADING } });
      try {
        const raw = await DunningService.getCampaignBlueprint(id);
        commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id, data: raw });
        commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id, data: { loadState: LOAD_STATE.LOADED, saveState: SAVE_STATE.SAVED } });
      } catch (e) {
        console.log('Error fetching campaign blueprint', e);
        commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id, data: { loadState: LOAD_STATE.ERROR_LOADING } });
      }
    },
    async [ACT.CREATE_DUNNING_CAMPAIGN_BLUEPRINT]({ commit, state }: IActionContext, options: { segmented: boolean }) {
      try {
        // generate a template
        const team = await OrgService.getMembers(state.user?.org as string);
        const admin = team.find((user) => user.role === 'ORG_OWNER');
        const user = admin || (state.user as IUser);
        const campaign = generateDunningSequence({
          from: user.email,
          replyTo: user.email,
          company: state.org?.name || '',
          senderName: user.name || '',
          ...options,
        });
        const campaignBlueprint = await DunningService.createCampaignBlueprint(campaign);
        return campaignBlueprint;
      } catch (e) {
        console.log('Error create campaign blueprint', e);
        return null;
      }
    },
    // persist changes to DB
    async [ACT.UPDATE_DUNNING_CAMPAIGN_BLUEPRINT]({ commit, state }: IActionContext, { id }: { id: string }) {
      try {
        // emails that haven't been deleted
        const emailsToKeep = state.campaignBlueprints[id].emails.filter((e) => !e.delete);
        commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id, data: { saveState: SAVE_STATE.SAVING, emails: emailsToKeep } });

        const updated = await DunningService.updateCampaignBlueprint(id, {
          data: state.campaignBlueprints[id],
        });
        const { newBlueprint } = updated;
        // change to saved if it hasn't been marked as unsaved while saving
        if (state.campaignBlueprints[id].saveState === SAVE_STATE.SAVING) {
          commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, {
            id,
            data: { saveState: SAVE_STATE.SAVED, updatedAt: newBlueprint.updatedAt },
          });
        }
        // eslint-disable-next-line no-restricted-syntax
        for (const email of state.campaignBlueprints[id].emails) {
          const emailId = email.guid;
          const data: Partial<ICampaignBlueprint> = { saveState: SAVE_STATE.SAVED };
          // Store _id of any newly created emails
          if (!email._id) {
            const newEmail = newBlueprint.emails.find((e) => e.guid === emailId);
            data._id = newEmail?._id;
          }
          commit(MUTATE.STORE_DUNNING_EMAIL_DATA, { campaignId: id, emailId, data });
        }
        return updated;
      } catch (e) {
        console.log(e);
        commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id, data: { saveState: SAVE_STATE.ERROR_SAVING } });
        return null;
      }
    },

    async [ACT.CLONE_DUNNING_CAMPAIGN_BLUEPRINT]({ state, commit, dispatch }: IActionContext, { id }: { id: string }) {
      try {
        const campaignBlueprint = await DunningService.cloneCampaignBlueprint(id);
        await dispatch(ACT.SYNC_DUNNING);
        return campaignBlueprint;
      } catch (error) {
        console.log('Error cloning campaign blueprint', error);
        return null;
      }
    },

    async [ACT.SEND_DUNNING_TEST_EMAIL](
      { state, commit, dispatch }: IActionContext,
      { campaignBlueprintId, emailBlueprintId, emailTo }: { campaignBlueprintId: string; emailBlueprintId: number; emailTo: string }
    ) {
      try {
        return await DunningService.sendTestEmail(campaignBlueprintId, emailBlueprintId, emailTo);
      } catch (error) {
        // @ts-ignore
        return { error: error.message || 'Unknown error!' };
      }
    },

    async [ACT.SCHEDULE_SAVE_DUNNING_CAMPAIGN_BLUEPRINT](
      { commit, dispatch, state, getters }: IActionContext,
      { id, delay }: { id: string; delay?: number }
    ) {
      const batchUpdateDelay = delay !== undefined ? delay : 2000;
      commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id, data: { saveState: SAVE_STATE.UNSAVED } });
      if (!getters.hasPermission('dunning:write')) return;
      const existingBatchUpdateTimeout = state.campaignBlueprints[id].batchUpdateTimeout;
      if (existingBatchUpdateTimeout) {
        clearTimeout(existingBatchUpdateTimeout);
      }
      const batchUpdate = () => {
        dispatch(ACT.UPDATE_DUNNING_CAMPAIGN_BLUEPRINT, { id });
      };
      const batchUpdateTimeout = setTimeout(batchUpdate, batchUpdateDelay);
      commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id, data: { batchUpdateTimeout } });
    },

    async [ACT.UPDATE_DUNNING_CAMPAIGN_BLUEPRINTS_ORDER](
      { state, getters }: IActionContext,
      { campaignBlueprints }: { campaignBlueprints: string[] }
    ) {
      try {
        if (!getters.hasPermission('dunning:write')) return;
        const orgId = getters.org._id;
        const response = await OrgService.reorderCampaignBlueprints(orgId, { campaignBlueprints });
        return true;
      } catch (error) {
        console.log('Error reordering campaign blueprints', error);
        return null;
      }
    },

    async [ACT.PUBLISH_DUNNING_CAMPAIGN_BLUEPRINT]({ state, dispatch, commit }: IActionContext, { id }: { id: string }) {
      try {
        // freeze operations during publish
        const campaignBlueprint = state.campaignBlueprints[id];
        // update if unsaved
        if (campaignBlueprint.saveState === SAVE_STATE.UNSAVED) {
          if (campaignBlueprint.batchUpdateTimeout) clearTimeout(campaignBlueprint.batchUpdateTimeout);
          await dispatch(ACT.UPDATE_DUNNING_CAMPAIGN_BLUEPRINT, { id });
        }
        const result = await DunningService.publishCampaignBlueprint(id);
        const { updatedAt, publishedAt, publishedCopies } = result.campaignBlueprint;
        commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, {
          id,
          data: {
            updatedAt,
            publishedAt,
            publishedCopies,
          },
        });
        return result;
      } catch (e) {
        console.log(e);
        commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id, data: { saveState: SAVE_STATE.ERROR_SAVING } });
        return null;
      }
    },

    async [ACT.CREATE_DUNNING_EXCLUSION]({ commit, state, dispatch }: IActionContext, { data }: { data: Omit<IExclusion, '_id'> }) {
      const { exclusion } = await DunningService.createExclusion(data);
      await dispatch(ACT.SYNC_DUNNING);
      return exclusion;
    },

    // persist changes to DB
    async [ACT.UPDATE_DUNNING_EXCLUSION]({ commit, state }: IActionContext, { id }: { id: string }) {
      try {
        commit(MUTATE.STORE_DUNNING_EXCLUSION_DATA, { id, data: { saveState: SAVE_STATE.SAVING } });

        const updated = await DunningService.updateExclusion(id, {
          data: state.dunningExclusions[id],
        });
        const { newExclusion } = updated;
        // change to saved if it hasn't been marked as unsaved while saving
        if (state.dunningExclusions[id].saveState === SAVE_STATE.SAVING) {
          commit(MUTATE.STORE_DUNNING_EXCLUSION_DATA, {
            id,
            data: { saveState: SAVE_STATE.SAVED, updatedAt: newExclusion.updatedAt },
          });
        }
        return updated;
      } catch (e) {
        console.log(e);
        commit(MUTATE.STORE_DUNNING_EXCLUSION_DATA, { id, data: { saveState: SAVE_STATE.ERROR_SAVING } });
        return null;
      }
    },

    async [ACT.CLONE_DUNNING_EXCLUSION]({ state, commit, dispatch }: IActionContext, { id }: { id: string }) {
      try {
        const exclusion = await DunningService.cloneExclusion(id);
        await dispatch(ACT.SYNC_DUNNING);
        return exclusion;
      } catch (error) {
        console.log('Error cloning campaign blueprint', error);
        return null;
      }
    },

    async [ACT.SCHEDULE_SAVE_DUNNING_EXCLUSION]({ commit, dispatch, state, getters }: IActionContext, { id, delay }: { id: string; delay?: number }) {
      const batchUpdateDelay = delay !== undefined ? delay : 2000;
      commit(MUTATE.STORE_DUNNING_EXCLUSION_DATA, { id, data: { saveState: SAVE_STATE.UNSAVED } });
      if (!getters.hasPermission('dunning:write')) return;
      const existingBatchUpdateTimeout = state.dunningExclusions[id].batchUpdateTimeout;
      if (existingBatchUpdateTimeout) {
        clearTimeout(existingBatchUpdateTimeout);
      }
      const batchUpdate = () => {
        dispatch(ACT.UPDATE_DUNNING_EXCLUSION, { id });
      };
      const batchUpdateTimeout = setTimeout(batchUpdate, batchUpdateDelay);
      commit(MUTATE.STORE_DUNNING_EXCLUSION_DATA, { id, data: { batchUpdateTimeout } });
    },

    async [ACT.UPDATE_DUNNING_EXCLUSIONS_ORDER]({ state, getters }: IActionContext, { exclusions }: { exclusions: string[] }) {
      try {
        if (!getters.hasPermission('dunning:write')) return;
        const orgId = getters.org._id;
        const response = await OrgService.reorderDunningExclusions(orgId, { exclusions });
        return true;
      } catch (error) {
        console.log('Error reordering dunning exclusions', error);
        return null;
      }
    },

    async [ACT.PUBLISH_DUNNING_EXCLUSION]({ state, dispatch, commit }: IActionContext, { id }: { id: string }) {
      try {
        // freeze operations during publish
        const exclusion = state.dunningExclusions[id];
        // update if unsaved
        if (exclusion.saveState === SAVE_STATE.UNSAVED) {
          if (exclusion.batchUpdateTimeout) clearTimeout(exclusion.batchUpdateTimeout);
          await dispatch(ACT.UPDATE_DUNNING_EXCLUSION, { id });
        }
        const result = await DunningService.publishExclusion(id);
        const { updatedAt, publishedAt, publishedCopies } = result.exclusion;
        commit(MUTATE.STORE_DUNNING_EXCLUSION_DATA, {
          id,
          data: {
            updatedAt,
            publishedAt,
            publishedCopies,
          },
        });
        return result;
      } catch (e) {
        console.log(e);
        commit(MUTATE.STORE_DUNNING_EXCLUSION_DATA, { id, data: { saveState: SAVE_STATE.ERROR_SAVING } });
        return null;
      }
    },

    async [ACT.DUNNING_WAITLIST_SIGNUP]({ commit, state }: IActionContext) {
      try {
        if (state.org?._id) {
          commit(MUTATE.SET_ORG_DETAILS, { dunningWaitlist: true });
          await OrgService.update(state.org._id, { dunningWaitlist: true });
        }
      } catch (e) {
        console.log(e);
      }
    },

    // async [ACT.DELETE_EMAIL_BLUEPRINT]({ commit, state }: IActionContext, { campaignId, emailId }: { campaignId: string; emailId: string }) {
    //   try {
    //     commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id: campaignId, data: { saveState: SAVE_STATE.SAVING } });
    //     const removed = await DunningService.deleteEmailBlueprint({ campaignId, emailId });
    //     const newCampaignEmails = state.campaignBlueprints[campaignId].emails.filter((e) => e.guid !== emailId);
    //     commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id: campaignId, data: { emails: newCampaignEmails, saveState: SAVE_STATE.UNSAVED } });
    //   } catch (e) {
    //     console.log(e);
    //     commit(MUTATE.STORE_DUNNING_CAMPAIGN_BLUEPRINT_DATA, { id: campaignId, data: { saveState: SAVE_STATE.ERROR_SAVING } });
    //   }
    // },
  },

  getters: {
    dunning(state: IState) {
      return state.org?.dunning;
    },
    dunningAvailable(state: IState, getters: any) {
      return getters.provider === 'stripe';
      // return state?.org?.dunningBeta;
    },
    dunningABTestsEnabled() {
      return false;
    },
    dunningCustomAttributesEnabled() {
      return true;
    },
    dunningWaitlist(state: IState) {
      return state?.org?.dunningWaitlist;
    },
    dunningOnboardState(state: IState, getters: any) {
      return {
        campaignCreated: !!getters.primaryCampaignBlueprintId,
        emailReady: getters.senderDomainReady,
        billingPageReady: getters.isBillingPageReady,
        activateReady: getters.isReadyToActivate,
        activated: state.org?.dunning?.enabled,
      };
    },
    senderDomainReady(state: IState) {
      return (state.org?.dunning?.senderDomains || []).some((domain: any) => domain?.returnPathVerified && domain?.dkimVerified);
    },
    subdomain(state: IState) {
      return state.org?.dunning?.checkoutPage?.ckSubdomain;
    },
    customDomain(state: IState) {
      // Singular, this is the PRIMARY custom domain to be linked on emails:
      return state.org?.dunning?.checkoutPage?.customDomain;
    },
    hostedBillingPage(state: IState, getters: any) {
      if (getters.customDomain?.url) {
        return `https://${getters.customDomain.url}`;
      }
      return null;
    },
    customDomains(state: IState) {
      // Plural, this is the list of custom domains:
      return state.org?.dunning?.checkoutPage?.customDomains;
    },
    hasCustomDomain(state: IState) {
      return !!(state.org?.dunning?.checkoutPage?.customDomains || []).some((domain: any) => !!domain?.url);
    },
    isCustomDomainReady(state: IState, getters: any) {
      // return getters.customDomains?.some((domain: any) => domain?.isResolving && domain?.hasSSL);
      // Check the primary custom domain, stored in the singular field customDomain:
      return getters.customDomain?.isResolving && getters.customDomain.hasSSL;
    },
    isBillingPageReady(state: IState, getters: any) {
      return getters.isCustomDomainReady && getters.subdomain;
    },
    isReadyToActivate(state: IState, getters: any) {
      return getters.isBillingPageReady && getters.senderDomainReady;
    },
    currentCampaignBlueprintId(state: IState) {
      return state.currentCampaignBlueprintId;
    },
    currentCampaignBlueprint(state: IState) {
      if (state.currentCampaignBlueprintId) {
        return state.campaignBlueprints[state.currentCampaignBlueprintId] || null;
      }
      return null;
    },
    currentCampaignBlueprintLoadState(state: IState, getters: any) {
      if (getters.currentCampaignBlueprint) {
        return getters.currentCampaignBlueprint.loadState;
      }
      return undefined;
    },
    currentDunningCampaignBlueprintSaveState(state: IState, getters: any) {
      if (getters.currentCampaignBlueprint) {
        return getters.currentCampaignBlueprint.saveState || SAVE_STATE.SAVED;
      }
      return undefined;
    },
    currentCampaignEmailBlueprints(state: IState, getters: any): string[] {
      if (getters.currentCampaignBlueprint) {
        return [...getters.currentCampaignBlueprint.emails].sort((a: IEmailBlueprint, b: IEmailBlueprint) => ascending(a.sendOnDay, b.sendOnDay));
      }
      return [];
    },
    currentCampaignEmailBlueprintMap(state: IState, getters: any) {
      if (getters.currentCampaignEmailBlueprints) {
        const emailBlueprints = getters.currentCampaignEmailBlueprints as IEmailBlueprint[];
        return emailBlueprints.reduce(
          (acc, emailBlueprint) => {
            acc[emailBlueprint.guid] = emailBlueprint;
            return acc;
          },
          {} as { [key: string]: IEmailBlueprint }
        );
      }
      return {};
    },
    currentEmailBlueprint(state: IState, getters: any) {
      try {
        return state.campaignBlueprints[getters.currentCampaignBlueprintId].emails.find(
          (emailBlueprint) => emailBlueprint.guid === state.currentEmailBlueprintId
        );
      } catch (e) {
        return null;
      }
    },
    baseEmailBlueprint(state: IState, getters: any) {
      const user = state.user as IUser;
      const campaign = generateDunningSequence({
        from: user.email,
        replyTo: user.email,
        company: state.org?.name || '',
        senderName: user.name || '',
        segmented: false, // does not matter, does not affect emails
      });
      const { emails } = campaign;
      return emails[0];
    },
    campaignBlueprints(state: IState) {
      return Object.values(state.campaignBlueprints);
    },
    primaryCampaignBlueprintId(state: IState) {
      return state.org?.dunning?.activeCampaign;
    },
    primaryCampaignBlueprint(state: IState, getters: any) {
      if (getters.primaryCampaignBlueprintId) {
        return state.campaignBlueprints[getters.primaryCampaignBlueprintId] || null;
      }
      return null;
    },
    mergeFields(): EmailMergeField[] {
      return [
        {
          label: 'First Name',
          id: 'FIRST_NAME',
          fallback: '',
        },
        {
          label: 'Card Brand',
          id: 'CARD_BRAND',
          fallback: '',
        },
        {
          label: 'Last 4 of Card',
          id: 'LAST_4',
          fallback: 'xxxx',
        },
        {
          label: 'Plan Name',
          id: 'PLAN_NAME',
          fallback: '',
        },
        {
          label: 'Discount Amount',
          id: 'DISCOUNT_AMOUNT',
          fallback: '',
        },
        {
          label: 'Discount Duration',
          id: 'DISCOUNT_DURATION',
          fallback: '',
        },
        {
          label: 'Discount Full Description',
          id: 'DISCOUNT_DESCRIPTION',
          fallback: '',
        },
        {
          label: 'Discount Short Description',
          id: 'DISCOUNT_DESCRIPTION_SHORT',
          fallback: '',
        },
        {
          label: 'Plan Charge Amount',
          id: 'PLAN_AMOUNT',
          fallback: '',
        },
        {
          label: 'Plan Charge Amount (Abbreviated)',
          id: 'PLAN_AMOUNT_SHORT',
          fallback: '',
        },
      ];
    },
    dunningExclusions(state: IState) {
      return Object.values(state.dunningExclusions);
    },
  },
};

export default dunningModule;
